import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import WeddingCard from '../components/wedding/WeddingCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar as CalendarIcon, Search, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const BrowseWeddings = () => {
  const { api } = useAuth();
  const { t, currencies } = useLanguage();
  
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location: '',
    dateFrom: null,
    dateTo: null,
    minPrice: '',
    maxPrice: '',
    country: '',
  });

  const fetchWeddings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location) params.append('location', filters.location);
      if (filters.dateFrom) params.append('date_from', format(filters.dateFrom, 'yyyy-MM-dd'));
      if (filters.dateTo) params.append('date_to', format(filters.dateTo, 'yyyy-MM-dd'));
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.country) params.append('country', filters.country);

      const response = await api.get(`/weddings?${params.toString()}`);
      setWeddings(response.data);
    } catch (error) {
      console.error('Failed to fetch weddings:', error);
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    fetchWeddings();
  }, [fetchWeddings]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchWeddings();
  };

  const resetFilters = () => {
    setFilters({
      location: '',
      dateFrom: null,
      dateTo: null,
      minPrice: '',
      maxPrice: '',
      country: '',
    });
    setTimeout(fetchWeddings, 0);
  };

  const countries = [
    { code: 'DE', name: 'Germany' },
    { code: 'US', name: 'United States' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AE', name: 'UAE' },
    { code: 'IN', name: 'India' },
    { code: 'CN', name: 'China' },
    { code: 'JP', name: 'Japan' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-secondary" data-testid="browse-weddings-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
              {t('wedding.search.title')}
            </h1>
            <p className="text-muted-foreground">
              Discover unique wedding experiences around the world
            </p>
          </div>

          {/* Filters */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Location */}
                  <div className="space-y-2">
                    <Label>{t('wedding.search.location')}</Label>
                    <Input
                      placeholder="e.g. Berlin, Paris..."
                      value={filters.location}
                      onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                      data-testid="filter-location"
                    />
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select
                      value={filters.country || "all"}
                      onValueChange={(value) => setFilters({ ...filters, country: value === "all" ? "" : value })}
                    >
                      <SelectTrigger data-testid="filter-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Countries</SelectItem>
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date From */}
                  <div className="space-y-2">
                    <Label>{t('wedding.search.date')} From</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="filter-date-from"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.dateFrom ? format(filters.dateFrom, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={filters.dateFrom}
                          onSelect={(date) => setFilters({ ...filters, dateFrom: date })}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Date To */}
                  <div className="space-y-2">
                    <Label>{t('wedding.search.date')} To</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="filter-date-to"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.dateTo ? format(filters.dateTo, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={filters.dateTo}
                          onSelect={(date) => setFilters({ ...filters, dateTo: date })}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Min Price */}
                  <div className="space-y-2">
                    <Label>Min {t('wedding.search.price')}</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.minPrice}
                      onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                      data-testid="filter-min-price"
                    />
                  </div>

                  {/* Max Price */}
                  <div className="space-y-2">
                    <Label>Max {t('wedding.search.price')}</Label>
                    <Input
                      type="number"
                      placeholder="1000"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                      data-testid="filter-max-price"
                    />
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-2 flex items-end gap-2">
                    <Button type="submit" className="flex-1 rounded-full" data-testid="filter-search">
                      <Search className="mr-2 h-4 w-4" />
                      {t('wedding.search.search')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetFilters}
                      className="rounded-full"
                      data-testid="filter-reset"
                    >
                      <X className="mr-2 h-4 w-4" />
                      {t('wedding.search.reset')}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="aspect-[4/3]" />
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-2" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : weddings.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground text-lg">{t('wedding.search.noResults')}</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {weddings.map((wedding) => (
                <WeddingCard key={wedding.id} wedding={wedding} />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BrowseWeddings;
